import { DesignHuddlePreview } from "../../../design-huddle/design-huddle-preview";
import { $createDesignHuddleNodeFromAssetOfClass, DesignHuddleNode } from "./design-huddle-node";
import { convertDesignHuddleImageElement, isDesignHuddleElement } from "./utils";

import type { UUID } from "../../../../utils/uuid";
import type { DesignHuddleCustomisations, SerializedDesignHuddleNode } from "./design-huddle-node";
import type { DOMConversionMap, LexicalNode } from "lexical";

export class DesignHuddleNodeMui extends DesignHuddleNode {
  __currentlyResizing = false;
  decorate(): React.JSX.Element {
    return (
      <DesignHuddlePreview
        nodeKey={this.__key}
        account_id={this.__account_id}
        project_id={this.__id}
        page_number={this.__page_number}
        page_id={this.__page_id}
        proportionOfMaxWidth={this.__proportionOfMaxWidth}
        aspect={this.__aspect}
        format={this.__format}
        must_be_cloned={this.__must_be_cloned}
        href={this.__href}
        preview_url={this.__preview_url}
        content_type={this.__contentType}
        asset_id={this.__sourceAssetId}
        title={this.__title}
      />
    );
  }
  static importJSON(serializedNode: SerializedDesignHuddleNode): DesignHuddleNode {
    return super.importJSON(serializedNode);
  }
  exportJSON(): SerializedDesignHuddleNode {
    return super.exportJSON();
  }
  static clone(node: DesignHuddleNode): DesignHuddleNode {
    return super.clone(node);
  }
  static getType(): string {
    return super.getType();
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: (node: HTMLElement) => {
        if (!isDesignHuddleElement(node)) return null;

        return {
          conversion: (n) => convertDesignHuddleImageElement(n, DesignHuddleNodeMui),
          priority: 0,
        };
      },
    };
  }
  setIsCurrentlyResizing(isCurrentlyResizing: boolean): void {
    const writable = this.getWritable();
    writable.__currentlyResizing = isCurrentlyResizing;
  }

  getIsCurrentlyResizing(): boolean {
    return this.__currentlyResizing;
  }
}

export function $createDesignHuddleNode(
  headless: boolean,
  account_id: string,
  id?: string,
  page_number?: number,
  customisations?: DesignHuddleCustomisations,
  proportionOfMaxWidth?: number,
  href?: string,
): DesignHuddleNode {
  return new (headless ? DesignHuddleNode : DesignHuddleNodeMui)(
    account_id,
    id,
    page_number,
    proportionOfMaxWidth,
    undefined,
    customisations,
    undefined,
    undefined,
    undefined,
    undefined,
    href,
  );
}

export function $createDesignHuddleNodeFromAsset(
  headless: boolean,
  account_id: string,
  project_id?: string,
  proportionOfMaxWidth?: number,
  aspect = 1,
  page_id?: string,
  preview_url?: string,
  asset_id?: UUID,
  check_tags = false,
): DesignHuddleNode {
  return $createDesignHuddleNodeFromAssetOfClass(
    headless ? DesignHuddleNode : DesignHuddleNodeMui,
    account_id,
    project_id,
    proportionOfMaxWidth,
    aspect,
    page_id,
    preview_url,
    asset_id,
    check_tags,
  );
}

export function $createDesignHuddleVideoNodeFromAsset(
  headless: boolean,
  account_id: string,
  project_id: string,
  thumbnail_url: string,
  video_url: string,
  proportionOfMaxWidth?: number,
  aspect = 1,
  asset_id?: UUID,
  title?: string,
): DesignHuddleNode {
  return new (headless ? DesignHuddleNode : DesignHuddleNodeMui)(
    account_id,
    project_id,
    undefined,
    proportionOfMaxWidth,
    aspect,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
    video_url,
    undefined,
    thumbnail_url,
    undefined,
    asset_id,
    false,
    "video",
    undefined,
    title,
  );
}

export function $isDesignHuddleNodeMui(
  node: LexicalNode | null | undefined,
): node is DesignHuddleNodeMui {
  return node instanceof DesignHuddleNodeMui;
}
